import { create } from 'zustand';
import api from '../api/client';
import i18n from '../../locales/i18n';
import { normalizeLanguageCode } from '../../locales/langConfig';

const applyUserLanguage = async (user) => {
  if (!user?.language) return;
  const locale = normalizeLanguageCode(user.language);
  if (i18n.resolvedLanguage !== locale) {
    await i18n.changeLanguage(locale);
  }
};

const normalizeCredits = (incoming, previous = {}) => {
  const monthly = Number(incoming?.monthly ?? previous?.monthly ?? 0);
  const extra = Number(incoming?.extra ?? previous?.extra ?? 0);
  const total = Number(incoming?.total ?? (monthly + extra));

  const initialCandidates = [
    Number(incoming?.initial),
    Number(previous?.initial),
    Number(previous?.total),
    total
  ].filter(Number.isFinite);

  const initial = initialCandidates.length ? Math.max(...initialCandidates) : total;
  return { monthly, extra, total, initial };
};

export const useAuthStore = create((set, get) => ({
  user: null,
  credits: null,
  usage: null,
  referral: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  hasInitialized: false,

  setUserLanguage: (language) => set((state) => ({
    user: state.user ? { ...state.user, language } : state.user,
  })),

  initialize: async (initData) => {
    set({ isLoading: true, hasInitialized: false });
    try {
      const response = await api.post('/auth/login');
      const {
        user,
        credits,
        usage,
        referral,
        accountDataAvailable,
      } = response.data;
      const prevCredits = get().credits;

      await applyUserLanguage(user);
      set({
        user,
        credits: accountDataAvailable === true
          ? normalizeCredits(credits, prevCredits || {})
          : null,
        usage: accountDataAvailable === true ? usage || null : null,
        referral: referral || null,
        isAuthenticated: true,
        isLoading: false,
        hasInitialized: true,
      });
    } catch (error) {
      set({
        credits: null,
        usage: null,
        isLoading: false,
        isAuthenticated: true,
        hasInitialized: true,
      });
    }
  },

  refreshUser: async () => {
    try {
      const response = await api.get("/auth/profile");
      const { user, credits, usage } = response.data;
      const prevCredits = get().credits;
      await applyUserLanguage(user);
      set({
        user: user || null,
        credits: normalizeCredits(credits, prevCredits || {}),
        usage: usage || { daily: 0, daily_limit: 10 },
        referral: response.data.referral || get().referral,
      });
      return response.data;
    } catch (error) {
      console.error("refreshUser error:", error);
      return null;
    }
  },

  restrictAccountData: () => set((state) => ({
    user: state.user
      ? {
          id: state.user.id,
          language: state.user.language,
        }
      : null,
    credits: null,
    usage: null,
  })),

  deductCredits: async (amount) => {
    set((state) => ({
      credits: state.credits
        ? {
            ...state.credits,
            total: Math.max(0, state.credits.total - amount)
          }
        : null,
    }));
  }
}));
